import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, apiService } from '../AdminContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  MessageSquare, Package, Wrench, Image, Star, Gift, TrendingUp,
  Phone, Clock, ArrowRight, Plus, Eye, RefreshCw, Activity
} from 'lucide-react';

export const Dashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState({});
  const [recentEnquiries, setRecentEnquiries] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [statsRes, enquiriesRes, activityRes] = await Promise.all([
        apiService.getDashboardStats(token),
        apiService.getRecentEnquiries(token, 5),
        apiService.getRecentActivity(token, 8)
      ]);
      setStats(statsRes.data);
      setRecentEnquiries(enquiriesRes.data);
      setRecentActivity(activityRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Total Enquiries', value: stats.total_enquiries || 0, subvalue: `${stats.new_enquiries || 0} new`, icon: MessageSquare, color: 'bg-green-500', link: '/admin/enquiries' },
    { label: 'Products', value: stats.total_products || 0, subvalue: `${stats.active_products || 0} active`, icon: Package, color: 'bg-sky-500', link: '/admin/products' },
    { label: 'Services', value: stats.total_services || 0, subvalue: `${stats.active_services || 0} active`, icon: Wrench, color: 'bg-orange-500', link: '/admin/services' },
    { label: 'Gallery', value: stats.total_projects || 0, subvalue: 'projects', icon: Image, color: 'bg-purple-500', link: '/admin/projects' },
    { label: 'Reviews', value: stats.total_reviews || 0, subvalue: `${stats.approved_reviews || 0} approved`, icon: Star, color: 'bg-yellow-500', link: '/admin/reviews' },
    { label: 'Offers', value: stats.total_offers || 0, subvalue: `${stats.active_offers || 0} active`, icon: Gift, color: 'bg-pink-500', link: '/admin/offers' },
  ];

  const quickActions = [
    { label: 'Add Product', icon: Package, path: '/admin/products?action=add', color: 'bg-sky-500' },
    { label: 'Add Project', icon: Image, path: '/admin/projects?action=add', color: 'bg-purple-500' },
    { label: 'Add Offer', icon: Gift, path: '/admin/offers?action=add', color: 'bg-pink-500' },
    { label: 'Edit Homepage', icon: TrendingUp, path: '/admin/homepage', color: 'bg-orange-500' },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-green-100 text-green-700';
      case 'contacted': return 'bg-blue-100 text-blue-700';
      case 'quoted': return 'bg-yellow-100 text-yellow-700';
      case 'closed': return 'bg-slate-100 text-slate-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-['Oswald'] text-2xl font-bold text-slate-900">Welcome back!</h1>
          <p className="text-slate-500">Here's what's happening with your business today.</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((stat) => (
          <Link key={stat.label} to={stat.link}>
            <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
              <CardContent className="p-4">
                <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center mb-3`}>
                  <stat.icon className="w-5 h-5 text-white" />
                </div>
                <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                <p className="text-xs text-slate-500">{stat.label}</p>
                <p className="text-xs text-slate-400 mt-1">{stat.subvalue}</p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {quickActions.map((action) => (
              <Link key={action.label} to={action.path}>
                <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2 hover:border-sky-300">
                  <div className={`w-10 h-10 ${action.color} rounded-lg flex items-center justify-center`}>
                    <action.icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm font-medium">{action.label}</span>
                </Button>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Enquiries */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-lg">Recent Enquiries</CardTitle>
            <Link to="/admin/enquiries">
              <Button variant="ghost" size="sm">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentEnquiries.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No enquiries yet</p>
            ) : (
              <div className="space-y-3">
                {recentEnquiries.map((enquiry) => (
                  <div key={enquiry.id} className="flex items-start justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900 truncate">{enquiry.name}</p>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(enquiry.status)}`}>
                          {enquiry.status}
                        </span>
                      </div>
                      <p className="text-sm text-sky-600">{enquiry.phone}</p>
                      {enquiry.service_needed && (
                        <p className="text-xs text-slate-500 truncate">{enquiry.service_needed}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <a href={`tel:${enquiry.phone}`}>
                        <Button size="icon" variant="ghost" className="h-8 w-8">
                          <Phone className="w-4 h-4" />
                        </Button>
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-lg">Recent Activity</CardTitle>
            <Link to="/admin/settings">
              <Button variant="ghost" size="sm">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentActivity.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No activity yet</p>
            ) : (
              <div className="space-y-3">
                {recentActivity.map((log, index) => (
                  <div key={log.id || index} className="flex items-start gap-3 p-2">
                    <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Activity className="w-4 h-4 text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-900">
                        <span className="font-medium">{log.user}</span> {log.details}
                      </p>
                      <p className="text-xs text-slate-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTime(log.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
